import { showErrorNotification } from "#/components/notifications";
import { logger } from "#/utils/logger";
import type { ApiResponse } from "#/types/api";
import { decryptAES, encryptAES } from "#/utils/aes";
import axios, {
  type AxiosInstance,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from "axios";

interface CustomAxiosInstance extends AxiosInstance {
  uploadFile: <T>(
    url: string,
    file: File,
    fieldName?: string,
    onProgress?: (progress: number) => void
  ) => Promise<T>;
  downloadFile: (data: BlobPart, filename: string, mimeType: string) => void;
}

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
}) as CustomAxiosInstance;

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const isLocal = import.meta.env.VITE_ENV === "local";
  const method = config.method?.toUpperCase();
  const supportsBody = ["POST", "PUT", "PATCH", "DELETE"].includes(
    method || ""
  );
  if (!supportsBody || !config.data) return config;

  // Skip encryption for FormData (file uploads)
  if (config.data instanceof FormData) return config;

  try {
    if (isLocal) {
      return config;
    } else {
      config.data = {
        data: encryptAES(config.data),
      };
      return config;
    }
  } catch (error) {
    logger.error("AES request body encryption failed:", error);
    return config;
  }
});

api.interceptors.response.use(
  <T>(response: AxiosResponse<ApiResponse<T>>): T => {
    if ((response.request as { responseType?: string } | undefined)?.responseType === "blob") {
      return response as unknown as T;
    }

    const isLocal = import.meta.env.VITE_ENV === "local";
    const data = response.data;

    if (data.success) {
      if (typeof data.data === "string") {
        if (isLocal) {
          // const decryptedRaw = decryptAES<T>(data.data);
          return response.data as T;
        } else {
          const decryptedRaw = decryptAES<T>(data.data);
          return {
            ...data,
            data: decryptedRaw,
          } as T;
        }
      } else {
        return isLocal ? (response.data as T) : data.data;
      }
    }

    let finalMessage: string;
    if (typeof data.message === "string") {
      finalMessage = data.message;
    } else if (typeof data.message === "object" && data.message !== null) {
      const messages: string[] = [];
      for (const key in data.message as object) {
        const fieldErrors: string = data.message[key];
        if (Array.isArray(fieldErrors)) {
          messages.push(...fieldErrors);
        } else if (typeof fieldErrors === "string") {
          messages.push(fieldErrors);
        }
      }
      finalMessage = messages.join("\n");
    } else {
      finalMessage = "Something went wrong";
    }

    showErrorNotification({
      message: finalMessage,
    });

    const error = new Error(finalMessage);
    Object.assign(error, { status: false, data: data.data });
    throw error;
  },
  (error: unknown) => {
    const axiosError = error as import("axios").AxiosError<{
      error?: string;
      message?: string;
      status?: boolean;
      data?: unknown;
    }>;
    const responseData = axiosError?.response?.data;

    // Handle client mismatch — redirect without toast
    if (
      axiosError?.response?.status === 403 &&
      responseData?.error === "CLIENT_MISMATCH"
    ) {
      window.location.href = "/client-mismatch";
      const mismatchError = new Error(responseData.message);
      Object.assign(mismatchError, { status: false, data: null });
      return Promise.reject(mismatchError);
    }

    const message = responseData?.message ?? "Something went wrong";
    showErrorNotification({
      message: message,
    });
    const rejectError = new Error(message);
    Object.assign(rejectError, {
      status: responseData?.status ?? false,
      data: responseData?.data ?? null,
    });
    return Promise.reject(rejectError);
  }
);

api.uploadFile = <T>(
  url: string,
  file: File,
  fieldName = "file",
  onProgress?: (progress: number) => void
): Promise<T> => {
  const formData = new FormData();
  formData.append(fieldName, file);
  return api.post(url, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
    onUploadProgress: (event) => {
      if (event.total) {
        const percent = Math.round((event.loaded * 100) / event.total);
        if (onProgress) onProgress(percent);
      }
    },
  });
};

api.downloadFile = (data: BlobPart, filename: string, mimeType: string) => {
  const blob = new Blob([data], { type: mimeType });
  const url = window.URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  window.URL.revokeObjectURL(url);
};

export default api;
